<template>
  <!--
    The amber tint marks a panel with something applied. It matters most while the panel is shut,
    because that is the case that gives no clue at all - the grid just looks empty.
  -->
  <div
    class="px-4 py-2 bg-white border rounded-lg cursor-pointer"
    :class="{
      hidden: !showContent && completeNoShow,
      'border-primary-100': !hasActiveCount,
      'border-amber-400 bg-amber-50': hasActiveCount && !showContent,
      'border-amber-400': hasActiveCount && showContent,
    }"
  >
    <div
      class="flex flex-row items-center justify-between transition-all"
      :class="{ 'pb-2 border-b border-primary-100': showContent }"
      v-if="props.showTitle"
      @click="toggleContent()"
    >
      <slot
        name="title"
        :toggleContent="toggleContent"
        :title="props.title ?? ''"
        :activeCount="props.activeCount ?? 0"
      >
        <div class="flex flex-row items-center gap-2">
          <div class="text-[14px] font-semibold cursor-pointer">
            {{ props.title }}
          </div>
          <!-- Says a filter is on even with the panel shut, so an empty grid is never a mystery. -->
          <span
            v-if="hasActiveCount"
            class="inline-flex items-center gap-1 px-2 py-[1px] rounded-full bg-amber-400 text-white text-[11px] font-semibold leading-4"
            :title="`${props.activeCount} filter${props.activeCount === 1 ? '' : 's'} applied`"
          >
            <i class="pi pi-filter-fill text-[9px]" />
            {{ props.activeCount }}
          </span>
        </div>
      </slot>
      <div class="flex flex-row items-center justify-center gap-2">
        <slot
          name="topright"
          v-if="
            props.showOptionsWhileCollapsed ||
            (!props.showOptionsWhileCollapsed && showContent)
          "
        >
        </slot>
        <button
          v-if="isCollapsible"
          class="flex items-center justify-center w-4 h-4"
        >
          <i
            class="transition-all duration-200 text-[14px] pi pi-chevron-down"
            :class="{
              'rotate-90': !showContent,
            }"
          />
        </button>
        <div v-else class="w-4 h-4"></div>
      </div>
    </div>
    <!-- <TransitionGroup name="p-toggleable-content" tag="div"> -->
    <div v-show="showContent && (loading || props.loading)" key="loading">
      LOADING...
    </div>
    <template v-if="showContent || (isLoaded && props.lazyLoad)">
      <div
        v-show="showContent && !loading && !props.loading"
        class=""
        key="content"
      >
        <slot></slot>
      </div>
    </template>
    <!-- </TransitionGroup> -->
  </div>
</template>

<script setup lang="ts">
const slots = defineSlots<{
  default(props: {}): any;
  title(props: {
    toggleContent(): void;
    title: string;
    activeCount: number;
  }): any;
  topright(props: {}): any;
}>();

const props = withDefaults(
  defineProps<{
    showByDefault?: boolean;
    loading?: boolean;
    title?: string;
    isCollapsible?: boolean;
    showTitle?: boolean;
    completeNoShow?: boolean;
    showOptionsWhileCollapsed?: boolean;
    lazyLoad?: boolean;
    /**
     * Number of active items inside this panel - a filter count, for instance. When above zero the
     * header shows a badge and the border is tinted, so the panel says something is applied even
     * while it is collapsed. Filters are meant to persist between sessions; this is what makes a
     * persisted one visible rather than leaving the grid looking empty for no apparent reason.
     */
    activeCount?: number;
  }>(),
  {
    showByDefault: false,
    isCollapsible: true,
    loading: false,
    showTitle: true,
    completeNoShow: false,
    showOptionsWhileCollapsed: true,
    lazyLoad: true,
    activeCount: 0,
  }
);

const hasActiveCount = computed(() => (props.activeCount ?? 0) > 0);

const showContent = defineModel<boolean>("showContent", {
  default: false,
});
if (props.showByDefault) {
  showContent.value = true;
}

const loading = ref<boolean>(false);
const isLoaded = ref<boolean>(showContent.value);

watch(
  () => showContent.value,
  (newValue) => {
    if (newValue) {
      emit("expanded", { isLoaded: isLoaded.value });
    }
    if (!isLoaded.value) {
      isLoaded.value = true;
    }
  }
);

const emit = defineEmits<{
  expanded: [event: { isLoaded: boolean }];
}>();

function toggleContent() {
  showContent.value = !showContent.value;
}

// Expose method to access slot content refs
defineExpose({
  toggleContent,
  showContent,
  isLoaded,
});
</script>

<style scoped></style>
